/**
 * @typedef {Object} Modal
 * @property {Element} modalContainer - The modal's dom container
 * @property {() => void} init - Initializes the modal
 * @property {(scrollToTop: boolean = false, focusInput: Element = null) => void} display - Shows the modal
 * @property {() => void} hide - hides the modal
 * @property {(element: Element, inPlace: boolean = false) => void} replace - Replaces the current modal with a new one
 */

/**
 * @typedef ModalInput
 * @property {Element} [modalContainer] - The initial modal dom container
 * @property {() => void} [init] - Optional initializer function that will be called
 * on every replace call
 */

import createElement from "../createElement";
import {waitUntil} from "../waitUntil";
import {loading, stopLoading} from "../loading";

// noinspection JSUnusedGlobalSymbols -- Imported via d.ts
/**
 * Creates a replaceable element from an initial dom element
 * and an initializer function
 * @template {ModalInput} T
 * @param {T} inputObject
 * @return {T|Modal|import('./_modal').Modals.Modal}
 * @deprecated
 */
function createModal(inputObject) {
	// Create empty initializer function if missing
	if (!inputObject.init) {
		inputObject.init = function () {
		};
	}

	if (!inputObject.modalContainer) {
		inputObject.modalContainer = createEmptyModal();
	}

	// Add replace function
	inputObject.replace = function (newModal, inPlace, preserveScrollPosition = false) {
		let scrollHistoryMap = {};
		if (preserveScrollPosition) {
			// Save vertical scroll position,
			// We trust that the position of the elements will be more or less
			// the same on the DOM tree, so we can restore the vertical scrolling
			// after loading the element back to the DOM
			scrollHistoryMap = {};
			iterateChildren(this.modalContainer, (element, id) => {
				if (element.scrollTop > 0 || element.scrollLeft) {
					scrollHistoryMap[id] = {
						top: element.scrollTop,
						left: element.scrollLeft,
					};
				}
			})
		}

		// If we are replacing in place and there
		// is an existing modal already setup
		if (inPlace && this.modalContainer.querySelector('.modal-content')) {
			// Replace the modal content only
			this.modalContainer.querySelector('.modal-content')
				.replaceWith(newModal.querySelector('.modal-content'))

			this.init();
		} else {
			// Fully replace the modal
			const oldModalContainer = this.modalContainer;

			// Replace on memory and initialize
			this.modalContainer = newModal;
			this.init();

			// Replace on dom
			oldModalContainer.replaceWith(this.modalContainer);
		}

		// noinspection DuplicatedCode
		if (preserveScrollPosition) {
			// Restore vertical scroll position
			iterateChildren(this.modalContainer, (element, id) => {
				if (scrollHistoryMap[id]) {
					element.scrollTop = scrollHistoryMap[id].top;
					element.scrollLeft = scrollHistoryMap[id].left;
				}
			})
		} else {
			// Scroll to the top
			setTimeout(() => {
				iterateChildren(this.modalContainer, element => {
					element.scrollTop = 0;
					element.scrollLeft = 0;
				})
			}, 16)
		}
	}

	// Add loading helpers
	inputObject.loading = () => {
		loading(inputObject.modalContainer.querySelector('.modal-content'));
	};

	inputObject.stopLoading = () => {
		stopLoading(inputObject.modalContainer.querySelector('.modal-content'));
	};

	// Initialize for first time if required
	if (inputObject.modalContainer.classList.contains('empty-modal') === false) {
		inputObject.init();
	}

	// Add display and hide helpers
	inputObject.display = function (scrollToTop = false, focusInput = null) {
		waitUntil(canDisplayModal, {
			msBetweenAttempts: 50,
		}).then(() => {
			$(this.modalContainer).modal('show');
			if (scrollToTop) {
				// Scroll to start
				waitUntil(() => {
					return this.modalContainer.classList.contains('show');
				}).then(() => {
					this.modalContainer.querySelector('.modal-body').scrollTop = 0;
				})
			}
			if (focusInput) {
				// Focus an input if possible
				waitUntil(() => {
					return this.modalContainer.classList.contains('show');
				}).then(() => {
					setTimeout(() => {
						// Wait a bit before focusing
						focusInput.focus();
					}, 512)
				})
			}
		})
	}

	inputObject.hide = function () {
		$(this.modalContainer).modal('hide');
	}

	// Add listeners functions
	const successListeners = [];

	inputObject.addSuccessListener = listener => {
		successListeners.push(listener)
	}

	inputObject.notifySuccessListeners = (data = undefined) => {
		successListeners.forEach(listener => {
			listener(inputObject, data)
		})
	}

	return inputObject;

	/**
	 * Methods
	 */

	function createEmptyModal() {
		const modalsContainer = document.querySelector('.modals')

		const newModal = createElement('<div class="modal fade empty-modal"></div>')

		modalsContainer.appendChild(newModal);

		return newModal;
	}

	// We don't want to display the modal if a loading overlay is active
	// or another modal is loading so we use this to check it
	// noinspection DuplicatedCode
	function canDisplayModal() {
		const loadingOverlay = document.querySelector('.loading');

		// noinspection UnnecessaryLocalVariableJS
		const loadingOverlayActive = loadingOverlay && loadingOverlay.style.display === 'block';

		// TODO: 	Check if another modal is loading
		// 			And try to add a delay after loading screen is gone
		return !loadingOverlayActive
	}

	/**
	 * Iterates recursively through all the children assigning an id
	 * to each child based on its position on the tree
	 *
	 * @param {Element} element
	 * @param {(element: Element,id: string) => void} handleChild
	 * @param id
	 */
	function iterateChildren(element, handleChild, id = '0') {
		handleChild(element, id);

		let index = 0;
		for (const child of element.children) {
			iterateChildren(child, handleChild, `${id}.${index}`);
			index++;
		}
	}
}

export {createModal}
